'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import Image from 'next/image'

interface HeroImageGridProps {
  images: string[]
}

/**
 * Cinematic hero background using property images.
 *
 * - Full-bleed images that slowly crossfade between each other
 * - Subtle Ken Burns zoom + pan on each image
 * - Gradient overlay from transparent top to solid bottom for text readability
 */
export function HeroImageGrid({ images }: HeroImageGridProps) {
  if (images.length === 0) return null

  return (
    <div className="absolute inset-0 overflow-hidden">
      <ImageCarousel images={images} />
      {/* Gradient overlay: let top show the image, fade to solid at bottom for text */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-black/60" />
      {/* Extra bottom fade where the search card sits */}
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-gray-50 dark:from-gray-900 to-transparent" />
    </div>
  )
}

/**
 * Full-bleed image carousel with crossfade + Ken Burns.
 * Two layers: current image fading out while next fades in.
 */
function ImageCarousel({ images }: { images: string[] }) {
  const [activeIndex, setActiveIndex] = useState(0)
  const [nextIndex, setNextIndex] = useState<number | null>(null)
  const [transitioning, setTransitioning] = useState(false)
  const activeRef = useRef(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  // Keep ref in sync with state
  useEffect(() => {
    activeRef.current = activeIndex
  }, [activeIndex])

  const advance = useCallback(() => {
    if (images.length <= 1) return
    const next = (activeRef.current + 1) % images.length
    setNextIndex(next)
    setTransitioning(true)
  }, [images.length])

  // Commit after crossfade completes
  useEffect(() => {
    if (!transitioning || nextIndex === null) return
    timerRef.current = setTimeout(() => {
      setActiveIndex(nextIndex)
      setNextIndex(null)
      setTransitioning(false)
    }, 2000) // match crossfade duration
    return () => clearTimeout(timerRef.current)
  }, [transitioning, nextIndex])

  // Auto-advance every 6 seconds
  useEffect(() => {
    if (images.length <= 1) return
    const interval = setInterval(advance, 6000)
    return () => clearInterval(interval)
  }, [advance, images.length])

  // Ken Burns: alternate zoom direction per image index
  const kenBurnsClass = (idx: number) => {
    const variants = [
      'animate-ken-burns-1',
      'animate-ken-burns-2',
      'animate-ken-burns-3',
    ]
    return variants[idx % variants.length]
  }

  return (
    <>
      {/* Active image */}
      <div
        className={`absolute inset-0 transition-opacity duration-[2000ms] ease-in-out ${
          transitioning ? 'opacity-0' : 'opacity-100'
        }`}
      >
        <div className={`absolute inset-0 ${kenBurnsClass(activeIndex)}`}>
          <Image
            // Invariant: parent renders the carousel only when images is non-empty,
            // and indices are always taken modulo images.length
            src={images[activeIndex]!}
            alt=""
            fill
            className="object-cover"
            sizes="100vw"
            quality={60}
            priority
          />
        </div>
      </div>

      {/* Next image (fades in underneath) */}
      {nextIndex !== null && (
        <div
          className={`absolute inset-0 transition-opacity duration-[2000ms] ease-in-out ${
            transitioning ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className={`absolute inset-0 ${kenBurnsClass(nextIndex)}`}>
            <Image
              // Invariant: nextIndex is computed modulo images.length
              src={images[nextIndex]!}
              alt=""
              fill
              className="object-cover"
              sizes="100vw"
              quality={60}
            />
          </div>
        </div>
      )}

      {/* Indicator dots */}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-1.5">
          {images.map((_, i) => (
            <span
              key={i}
              className={`block w-1.5 h-1.5 rounded-full transition-all duration-500 ${
                i === activeIndex
                  ? 'bg-white w-4'
                  : 'bg-white/40'
              }`}
            />
          ))}
        </div>
      )}
    </>
  )
}
